// @flow

import React from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import _ from 'lodash';
import FunnelStep from 'client/components/funnelStep';
import Button from 'client/components/button/button';
import Translate from 'client/components/translate';
import Notification from 'client/components/notification';
import {
  createZone,
  extoleApplicationTypesMap,
  extoleZoneNamesMap,
} from 'client/components/extole/extoleCreateZoneCard';
import { Tracking } from 'web-core/src/tracking';
import type { UserType } from 'web-core/types/user';
import type { MemberType } from 'web-core/types/member';
import type { RouterType } from 'types/react-router.flow';

import { isActiveCrossSell } from 'client/utils/ifpCrossSell';
import { saveMembersToApp, updateMembers } from 'client/actions/members';
import 'client/styles/selectionList.scss';

import RenderNewMembers from './member/renderNewMembers';
import RenderSelectMembers from './member/renderSelectMembers';

import '../styles/members.scss';

type Props = {
  user: UserType,
  visionApp: {
    app_id: string,
    members: Array<MemberType>,
  },
  saveMembersToApp: Function,
  members: Array<MemberType> | MemberType,
  crossSellMembers?: Array<MemberType>,
  router: RouterType,
  updateMembers: Function,
  isActiveCrossSell: boolean,
};

const mapStateToProps = (state) => {
  const visionApp = _.find(state.ifpApps, { type: 'vision' });
  const appMembers = _.get(visionApp, 'members');

  return {
    user: state.user,
    visionApp,
    members: state.members,
    initialValues: {
      members: state.members,
    },
    crossSellMembers: _.filter(state.members, member => _.find(appMembers, { id: member.id })),
    isActiveCrossSell: isActiveCrossSell(state, 'vision'),
  };
};

const isComplete = member => member.age && member.type && member.gender && member.id;

export class ApplicantsPage extends React.Component<Props> {
  static defaultProps = {
    crossSellMembers: [],
  };

  constructor(props) {
    super(props);

    let initialAppMembers = props.visionApp.members;
    let isEditing = false;
    let incompletePrimary;

    // Logged out user - create primary and add to app members
    if (!initialAppMembers) {
      initialAppMembers = [
        {
          id: props.user.primaryMemberId,
          type: 'primary',
        },
      ];
    } else if (initialAppMembers.length === 0 && props.members) {
      // No members saved to app yet

      // Check if primary member info has age, gender, type, and id
      const primaryMember = _.find(props.members, { type: 'primary' });
      if (primaryMember && !isComplete(primaryMember)) {
        isEditing = true;
        incompletePrimary = primaryMember;
      }

      // Add all members as default checked on the app
      initialAppMembers = props.members;
    }
    const appMembers = _.clone(initialAppMembers);

    this.state = {
      isOneSelected: appMembers.length > 0,
      isEditing,
      appMembers,
      incompletePrimary,
    };
  }

  componentDidMount() {
    const { user } = this.props;

    createZone({
      zoneName: extoleZoneNamesMap.ONBOARDING_STARTED,
      data: {
        application_type: extoleApplicationTypesMap.VISION,
        ...(user.email && {
          partner_user_id: user.personId,
          email: user.email,
        }),
      },
    });
  }

  setSubmitting(state) {
    this.setState({
      isSubmitting: state,
    });
  }

  saveAppMembers = () => {
    const { visionApp, user } = this.props;
    const { appMembers } = this.state;

    const coveredMembers = appMembers.map(member => ({ ...member, status: 'coverage' }));
    this.setSubmitting(true);

    return this.props
      .saveMembersToApp(coveredMembers, visionApp.app_id, user)
      .then((response) => {
        if (response && response.members) {
          Tracking.setPeopleProps({
            'Vision Members': response.members.length,
          });
          this.props.router.push('/onboarding/plans');
        }
      })
      .catch(() => this.setSubmitting(false));
  };

  renderButton() {
    const { members } = this.props;
    if (!this.state.isEditing && members.length) {
      return (
        <div className="text-center" style={{ padding: '2.5rem 1rem' }}>
          <Button
            className={cx(
              'cta-button',
              { disabled: !this.state.isOneSelected },
              { submitting: this.state.isSubmitting },
            )}
            clickHandler={this.saveAppMembers}>
            <Translate path="common.continue" />
          </Button>
        </div>
      );
    }
    return null;
  }

  render() {
    const { members, router, crossSellMembers } = this.props;
    const hasSpouse = !!_.find(members, { type: 'spouse' });

    return (
      <FunnelStep index={1} descriptKey="vision.nav.applicants">
        <div className="members-page">
          <Notification theme="info" border borderPosition="bottom">
            <div
              className="text-center"
              style={{
                maxWidth: '36rem',
                margin: 'auto',
                fontSize: '.875rem',
              }}>
              <span className="light">Stride built a vision plan that provides </span>
              <strong>the most coverage at the best price.</strong>
            </div>
          </Notification>

          <div className="title text-center">
            {this.state.incompletePrimary ? (
              <Translate path="vision.members.noPrimaryInfo" />
            ) : (
              <Translate
                path={`vision.members.${members.length ? 'selectMembers' : 'newMembers'}`} />
            )}
          </div>

          <div className="applicants margin-bottom-2">
            {members.length ? (
              <div>
                {!this.state.incompletePrimary && (
                  <RenderSelectMembers
                    members={
                      this.props.isActiveCrossSell && crossSellMembers.length
                        ? crossSellMembers
                        : members
                    }
                    appMembers={this.state.appMembers}
                    changeHandler={(appMembers) => {
                      this.setState({
                        isOneSelected: appMembers.length > 0,
                        appMembers,
                      });
                    }} />
                )}
                <RenderNewMembers
                  restrictSingle
                  incompleteMember={this.state.incompletePrimary}
                  hasSpouse={hasSpouse}
                  disableAddMembers={this.props.isActiveCrossSell}
                  changeHandler={(isEditing, newMembersArray) => {
                    if (newMembersArray && newMembersArray.length) {
                      // Get primary member and check if information is complete
                      if (this.state.incompletePrimary) {
                        const savedPrimary = _.find(newMembersArray, {
                          id: this.state.incompletePrimary.id,
                        });
                        if (savedPrimary && isComplete(savedPrimary)) {
                          this.props.updateMembers({ members: newMembersArray });
                          this.setState({
                            incompletePrimary: null,
                            appMembers: newMembersArray,
                            isEditing,
                          });
                        }
                      } else {
                        this.setState({
                          appMembers: [...this.state.appMembers, ...newMembersArray],
                        });
                      }
                    }
                    return this.setState({ isEditing });
                  }} />
              </div>
            ) : (
              <RenderNewMembers saveHandler={() => router.push('/onboarding/plans')} />
            )}
          </div>

          {this.renderButton()}
        </div>
      </FunnelStep>
    );
  }
}

export default connect(mapStateToProps, {
  saveMembersToApp,
  updateMembers,
})(ApplicantsPage);
